<template #default>
  <div class="page-view">
    <a-card class="page-filter" :bordered="false">
      <a-form layout="inline" @submit.prevent="onSearch">
        <a-form-item label="Fund Code">
          <a-input
            v-model:value="fundCode"
            placeholder="Enter fund code..."
            @pressEnter="onSearch"
          />
        </a-form-item>
        <a-form-item label="Status">
          <a-select
            v-model:value="active"
            style="width: 120px"
            allow-clear
            placeholder="All"
          >
            <a-select-option value="true">Active</a-select-option>
            <a-select-option value="false">InActive</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item>
          <div class="page-actions">
            <a-button type="primary" @click="onSearch" :loading="loading"
              >Search</a-button
            >
            <a-button @click="showAddModal">Add</a-button>
          </div>
        </a-form-item>
      </a-form>
    </a-card>

    <div class="page-grid page-grid--h360">
      <AgGridVue
        style="width: 100%; height: 100%"
        :theme="themeBalham"
        :loading="loading"
        :columnDefs="columnDefs"
        :rowData="rowData"
        :defaultColDef="defaultColDef"
      />
    </div>

    <!-- Add/Edit Fund Modal -->
    <a-modal
      class="page-modal"
      v-model:visible="addModalVisible"
      :title="isEdit ? 'Edit Fund' : 'Add Fund'"
      @ok="isEdit ? handleEditFund() : handleAddFund()"
      @cancel="handleAddCancel"
      :confirmLoading="addLoading"
      destroyOnClose
    >
      <a-form
        :model="addForm"
        :label-col="{ span: 6 }"
        :wrapper-col="{ span: 16 }"
        layout="horizontal"
      >
        <a-form-item label="Fund Code" required>
          <a-input
            v-model:value="addForm.fundCode"
            placeholder="Enter fund code"
            :disabled="isEdit"
            @input="onFundCodeInput"
          />
        </a-form-item>
        <a-form-item label="Fund Desc" required>
          <a-input
            v-model:value="addForm.fundDesc"
            placeholder="Enter fund description"
          />
        </a-form-item>
        <a-form-item label="Is Platform">
          <a-switch v-model:checked="addForm.isPlatform" />
        </a-form-item>
        <a-form-item label="Active">
          <a-switch v-model:checked="addForm.isActive" :disabled="isEdit" />
        </a-form-item>
        <a-form-item label="Is Real">
          <a-switch v-model:checked="addForm.isReal" />
        </a-form-item>
        <a-form-item label="Currency" required>
          <a-select
            v-model:value="addForm.baseCurrencyId"
            placeholder="Select currency"
            :show-search="true"
            :allow-clear="true"
            :filter-option="filterCurrencyOption"
            :disabled="
              isEdit &&
              addForm.baseCurrencyId !== null &&
              addForm.baseCurrencyId !== undefined &&
              addForm.baseCurrencyId !== ''
            "
          >
            <a-select-option
              v-for="item in currencyList"
              :key="item.currencyCode"
              :value="item.currencyId"
            >
              {{ item.currencyCode }} - {{ item.currencyDesc }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <!-- <a-form-item label="Use New Acct">
          <a-switch v-model:checked="addForm.useNewAcct" />
        </a-form-item> -->
        <a-form-item label="Country" required>
          <a-select
            v-model:value="addForm.countryId"
            placeholder="Select country"
            :show-search="true"
            :allow-clear="true"
            :filter-option="filterCountryOption"
            :disabled="
              isEdit &&
              addForm.countryId !== null &&
              addForm.countryId !== undefined &&
              addForm.countryId !== ''
            "
          >
            <a-select-option
              v-for="item in countryList"
              :key="item.countryCode"
              :value="item.countryId"
            >
              {{ item.countryCode }} - {{ item.countryDesc }}
            </a-select-option>
          </a-select>
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script setup>
import { ref, computed, defineExpose } from 'vue'
import { useStore } from 'vuex'
import { themeBalham } from 'ag-grid-community'

import { message } from 'ant-design-vue'
import fundView_ColumnDefs from '@/views/reference/fund/FundView_ColumnDefs'
import AgEditCellRenderer from '@/components/AgEditCellRenderer.vue'
import fund from '@/api/fund'
// import reference from '@/api/reference'

const store = useStore()
// const rowSelection = {
//   mode: 'singleRow',
// }
const defaultColDef = {
  filter: true,
  sortable: true,
  resizable: true,
}

defineExpose({
  AgEditCellRenderer,
})

const fundCode = ref('')
const active = ref(undefined)
const loading = ref(false)
const rowData = ref([])

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || fallback

const currencyList = computed(() => {
  const currencies = store.getters['reference/getCurrencyList'] || []
  return [...currencies].sort((a, b) =>
    a.currencyCode.localeCompare(b.currencyCode)
  )
})

const countryList = computed(() => {
  const countries = store.getters['reference/getCountryList'] || []
  return [...countries].sort((a, b) =>
    a.countryCode.localeCompare(b.countryCode)
  )
})

const columnDefs = fundView_ColumnDefs.map((col) => {
  if (col.field === 'edit') {
    return {
      ...col,
      cellRendererParams: {
        showEditModal: (data) => showEditModal(data),
      },
    }
  }
  return col
})

const onSearch = async () => {
  loading.value = true
  try {
    let isActive
    if (active.value === 'true') isActive = true
    else if (active.value === 'false') isActive = false

    const qry = {
      fundCode: fundCode.value.trim(),
      isActive,
    }
    const resp = await fund.getFundList(qry)
    const data = resp.data
    rowData.value = Array.isArray(data) ? data : []
    if (rowData.value.length === 0) {
      message.info('No data found.')
    }
  } catch (e) {
    message.error(getErrorMessage(e, 'Query failed'))
  } finally {
    loading.value = false
  }
}

const addModalVisible = ref(false)
const addLoading = ref(false)
const isEdit = ref(false)

const createEmptyForm = () => ({
  fundCode: '',
  fundDesc: '',
  isPlatform: true,
  isActive: true,
  isReal: true,
  baseCurrencyId: 5,
  // useNewAcct: true,
  countryId: 78,
})

const addForm = ref(createEmptyForm())

const showAddModal = () => {
  isEdit.value = false
  addForm.value = createEmptyForm()
  addModalVisible.value = true
}

const showEditModal = (event) => {
  const data = event?.data || event
  isEdit.value = true
  addForm.value = { ...data }
  addModalVisible.value = true
}

const handleAddCancel = () => {
  addModalVisible.value = false
}

const validateRequired = () => {
  const { fundCode, fundDesc, baseCurrencyId, countryId } = addForm.value
  if (!fundCode || !fundDesc || !baseCurrencyId || !countryId) {
    message.warning('Please fill all required fields.')
    return false
  }
  return true
}

const handleAddFund = async () => {
  if (!validateRequired()) return
  addLoading.value = true
  try {
    await fund.createFund({ ...addForm.value })
    message.success('Add success')
    addModalVisible.value = false
    onSearch()
  } catch (e) {
    message.error(getErrorMessage(e, 'Add failed'))
  } finally {
    addLoading.value = false
  }
}

const handleEditFund = async () => {
  if (!validateRequired()) return
  addLoading.value = true
  try {
    await fund.updateFund({ ...addForm.value })
    message.success('Edit success')
    addModalVisible.value = false
    onSearch()
  } catch (e) {
    message.error(getErrorMessage(e, 'Edit failed'))
  } finally {
    addLoading.value = false
  }
}

const onFundCodeInput = (event) => {
  const value = event?.target?.value || ''
  addForm.value.fundCode = value.toUpperCase()
}

const filterCurrencyOption = (input, option) =>
  option.key.toLowerCase().includes(input.toLowerCase())

const filterCountryOption = (input, option) =>
  option.key.toLowerCase().includes(input.toLowerCase())
</script>
